import { useQuery, useMutation } from '@apollo/client';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

import {
  MY_WORKSPACES_QUERY,
  WORKSPACE_MEMBERS_QUERY,
  CREATE_WORKSPACE_MUTATION,
} from '../services/graphql';
import { useAuth } from './AuthContext';

/**
 * Workspace role
 */
export type WorkspaceRole = 'OWNER' | 'MEMBER';

/**
 * Workspace type
 */
export interface Workspace {
  id: string;
  name: string;
  ownerUserId: string;
  createdAt: string;
  updatedAt: string;
  myRole: WorkspaceRole | null;
  memberCount: number | null;
}

/**
 * Workspace member type
 */
export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  email: string;
  publicKey: string | null;
  role: WorkspaceRole;
  joinedAt: string;
}

/**
 * Context type
 */
interface WorkspaceContextType {
  /** All workspaces the user belongs to */
  workspaces: Workspace[];
  /** Currently selected workspace */
  currentWorkspace: Workspace | null;
  /** Whether workspaces are loading */
  isLoading: boolean;
  /** Whether user needs to create their first workspace */
  needsWorkspaceSetup: boolean;
  /** Current user's role in current workspace */
  currentRole: WorkspaceRole | null;
  /** Is current user the owner of current workspace */
  isOwner: boolean;
  /** Members of current workspace (lazy-loaded) */
  members: WorkspaceMember[];
  /** Whether members are loading */
  membersLoading: boolean;
  /** Switch to a different workspace */
  switchWorkspace: (workspaceId: string) => void;
  /** Create a new workspace */
  createWorkspace: (name: string) => Promise<Workspace>;
  /** Refresh workspaces list */
  refetchWorkspaces: () => Promise<void>;
  /** Load members for current workspace */
  loadMembers: () => Promise<void>;
  /** Refetch members */
  refetchMembers: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const CURRENT_WORKSPACE_KEY = 'amnpass_current_workspace_id';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(() => {
    // Try to restore from localStorage
    return localStorage.getItem(CURRENT_WORKSPACE_KEY);
  });
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Fetch workspaces
  const {
    data: workspacesData,
    loading: workspacesLoading,
    refetch: refetchWorkspacesQuery,
  } = useQuery(MY_WORKSPACES_QUERY, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const [createWorkspaceMutation] = useMutation(CREATE_WORKSPACE_MUTATION);

  const workspaces: Workspace[] = workspacesData?.myWorkspaces || [];

  // Determine current workspace
  const currentWorkspace =
    workspaces.find((w) => w.id === currentWorkspaceId) || workspaces[0] || null;

  // Update localStorage when current workspace changes
  useEffect(() => {
    if (currentWorkspace?.id) {
      localStorage.setItem(CURRENT_WORKSPACE_KEY, currentWorkspace.id);
      setCurrentWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id]);

  // Clear workspace on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentWorkspaceId(null);
      setMembers([]);
    }
  }, [isAuthenticated]);

  // Check if user needs workspace setup
  const needsWorkspaceSetup = isAuthenticated && !workspacesLoading && workspaces.length === 0;

  // Current role in workspace
  const currentRole = currentWorkspace?.myRole || null;
  const isOwner = currentRole === 'OWNER';

  // Switch workspace
  const switchWorkspace = useCallback((workspaceId: string) => {
    setCurrentWorkspaceId(workspaceId);
    setMembers([]); // Clear members when switching
  }, []);

  // Create workspace
  const createWorkspace = useCallback(
    async (name: string): Promise<Workspace> => {
      const { data } = await createWorkspaceMutation({
        variables: { name },
        refetchQueries: [{ query: MY_WORKSPACES_QUERY }],
      });

      const newWorkspace = data?.createWorkspace;
      if (newWorkspace) {
        setCurrentWorkspaceId(newWorkspace.id);
      }
      return newWorkspace;
    },
    [createWorkspaceMutation]
  );

  // Refetch workspaces
  const refetchWorkspaces = useCallback(async () => {
    await refetchWorkspacesQuery();
  }, [refetchWorkspacesQuery]);

  // Load members for current workspace
  const loadMembers = useCallback(async () => {
    if (!currentWorkspace || !isOwner) {
      setMembers([]);
      return;
    }

    setMembersLoading(true);
    try {
      // Using Apollo client directly for lazy loading
      const { apolloClient } = await import('../services/apollo');
      const { data } = await apolloClient.query({
        query: WORKSPACE_MEMBERS_QUERY,
        variables: { workspaceId: currentWorkspace.id },
        fetchPolicy: 'network-only',
      });
      setMembers(data?.workspaceMembers || []);
    } catch (err) {
      console.error('Failed to load workspace members:', err);
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [currentWorkspace, isOwner]);

  // Refetch members
  const refetchMembers = useCallback(async () => {
    await loadMembers();
  }, [loadMembers]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        isLoading: workspacesLoading,
        needsWorkspaceSetup,
        currentRole,
        isOwner,
        members,
        membersLoading,
        switchWorkspace,
        createWorkspace,
        refetchWorkspaces,
        loadMembers,
        refetchMembers,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

